import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  FileBarChart,
  Download,
  Calendar,
  Loader2,
  TrendingUp,
  Clock,
  XCircle,
  CheckCircle2,
  Target,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

export default function Reports() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportDate, setReportDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedShift, setSelectedShift] = useState("");
  const [shiftReport, setShiftReport] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [activeShift, setActiveShift] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    // Get active shift info
    const fetchActiveShift = async () => {
      try {
        const response = await axios.get(`${API}/shifts/active`);
        setActiveShift(response.data);
      } catch (error) {
        console.error("Error fetching shift:", error);
      }
    };
    fetchActiveShift();
  }, []);

  const fetchShiftReport = async () => {
    setLoading(true);
    try {
      const params = { date: reportDate };
      if (selectedShift) params.shift = selectedShift;
      
      const response = await axios.get(`${API}/reports/shift`, { params });
      setShiftReport(response.data);
    } catch (error) {
      toast.error("Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = {};
      if (historyFilters.start_date) params.start_date = historyFilters.start_date;
      if (historyFilters.end_date) params.end_date = historyFilters.end_date;
      
      const response = await axios.get(`${API}/reports/history`, { params });
      setHistoryData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShiftReport();
  }, []);

  const formatTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}min`;
  };

  const generatePDF = () => {
    if (!shiftReport) return;

    const shiftDuration = activeShift?.active 
      ? formatTime(activeShift.elapsed_minutes) 
      : "N/A";

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Turno - ${reportDate}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            padding: 30px; 
            color: #1a1a1a;
            line-height: 1.5;
          }
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: flex-start;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 25px;
          }
          .logo { height: 60px; }
          .header-info { text-align: right; }
          .header-info h1 { font-size: 22px; color: #2563eb; margin-bottom: 5px; }
          .header-info p { font-size: 12px; color: #666; }
          .operator-info {
            background: #f0f9ff;
            border: 1px solid #bae6fd;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 25px;
          }
          .operator-info h3 { color: #0369a1; margin-bottom: 10px; font-size: 14px; }
          .operator-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
          .operator-item label { font-size: 10px; color: #666; display: block; }
          .operator-item span { font-size: 13px; font-weight: 600; color: #1a1a1a; }
          .summary-grid { 
            display: grid; 
            grid-template-columns: repeat(4, 1fr); 
            gap: 15px; 
            margin-bottom: 25px; 
          }
          .stat-card { 
            background: #f8fafc; 
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 15px; 
            text-align: center;
          }
          .stat-card.highlight { background: #f0fdf4; border-color: #86efac; }
          .stat-card.warning { background: #fef9c3; border-color: #fde047; }
          .stat-card.danger { background: #fef2f2; border-color: #fecaca; }
          .stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
          .stat-value { font-size: 26px; font-weight: 700; color: #1e293b; }
          .stat-value.green { color: #16a34a; }
          .stat-value.red { color: #dc2626; }
          .stat-value.blue { color: #2563eb; }
          .section { margin-bottom: 25px; }
          .section-title { 
            font-size: 14px; 
            font-weight: 700; 
            color: #1e293b; 
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e2e8f0;
          }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { 
            background: #f1f5f9; 
            padding: 10px 8px; 
            text-align: left; 
            font-weight: 600;
            color: #475569;
            border-bottom: 2px solid #e2e8f0;
          }
          td { 
            padding: 10px 8px; 
            border-bottom: 1px solid #f1f5f9;
            color: #334155;
          }
          .oee-section {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: white;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
          }
          .oee-section h3 { font-size: 14px; margin-bottom: 15px; opacity: 0.9; }
          .oee-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
          .oee-item { text-align: center; }
          .oee-label { font-size: 10px; opacity: 0.8; text-transform: uppercase; }
          .oee-value { font-size: 28px; font-weight: 700; }
          .progress-bar {
            height: 8px;
            background: rgba(255,255,255,0.2);
            border-radius: 4px;
            margin-top: 8px;
            overflow: hidden;
          }
          .progress-fill { height: 100%; background: white; border-radius: 4px; }
          .footer { 
            margin-top: 30px; 
            padding-top: 15px;
            border-top: 1px solid #e2e8f0;
            font-size: 10px; 
            color: #94a3b8;
            display: flex;
            justify-content: space-between;
          }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-break { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LOGO_URL}" alt="Logo" class="logo" />
          <div class="header-info">
            <h1>Relatório de Fim de Turno</h1>
            <p>Data: ${reportDate}</p>
            <p>Turno: ${selectedShift || 'Todos'}</p>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>

        <div class="operator-info">
          <h3>👤 Informação do Operador</h3>
          <div class="operator-grid">
            <div class="operator-item">
              <label>Nome</label>
              <span>${user?.name || 'N/A'}</span>
            </div>
            <div class="operator-item">
              <label>Código</label>
              <span>${user?.user_code || 'N/A'}</span>
            </div>
            <div class="operator-item">
              <label>Função</label>
              <span>${user?.role === 'admin' ? 'Administrador' : user?.role === 'operator' ? 'Operador' : user?.role === 'quality' ? 'Qualidade' : user?.role}</span>
            </div>
            <div class="operator-item">
              <label>Tempo no Turno</label>
              <span>${shiftDuration}</span>
            </div>
          </div>
        </div>

        <div class="oee-section no-break">
          <h3>📊 Indicadores de Eficiência (OEE)</h3>
          <div class="oee-grid">
            <div class="oee-item">
              <div class="oee-label">Disponibilidade</div>
              <div class="oee-value">${shiftReport.summary.availability}%</div>
              <div class="progress-bar"><div class="progress-fill" style="width: ${shiftReport.summary.availability}%"></div></div>
            </div>
            <div class="oee-item">
              <div class="oee-label">Performance</div>
              <div class="oee-value">${shiftReport.summary.performance}%</div>
              <div class="progress-bar"><div class="progress-fill" style="width: ${Math.min(shiftReport.summary.performance, 100)}%"></div></div>
            </div>
            <div class="oee-item">
              <div class="oee-label">Qualidade</div>
              <div class="oee-value">${shiftReport.summary.quality}%</div>
              <div class="progress-bar"><div class="progress-fill" style="width: ${shiftReport.summary.quality}%"></div></div>
            </div>
            <div class="oee-item">
              <div class="oee-label">OEE Total</div>
              <div class="oee-value">${shiftReport.summary.oee}%</div>
              <div class="progress-bar"><div class="progress-fill" style="width: ${shiftReport.summary.oee}%"></div></div>
            </div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="stat-card">
            <div class="stat-label">Planeado</div>
            <div class="stat-value">${shiftReport.summary.planned_quantity.toLocaleString()}</div>
          </div>
          <div class="stat-card highlight">
            <div class="stat-label">Produção Boa</div>
            <div class="stat-value green">${shiftReport.summary.good_quantity.toLocaleString()}</div>
          </div>
          <div class="stat-card danger">
            <div class="stat-label">Rejeitado</div>
            <div class="stat-value red">${shiftReport.summary.rejected_quantity.toLocaleString()}</div>
          </div>
          <div class="stat-card warning">
            <div class="stat-label">Taxa Rejeição</div>
            <div class="stat-value">${shiftReport.summary.rejection_rate}%</div>
          </div>
        </div>

        <div class="summary-grid">
          <div class="stat-card">
            <div class="stat-label">Tempo Produtivo</div>
            <div class="stat-value blue">${formatTime(shiftReport.summary.productive_minutes)}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Tempo Parado</div>
            <div class="stat-value red">${formatTime(shiftReport.summary.stop_time_minutes)}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">MP Teórica (kg)</div>
            <div class="stat-value">${shiftReport.raw_materials?.theoretical_kg || 0}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">MP Consumida (kg)</div>
            <div class="stat-value">${shiftReport.raw_materials?.consumed_kg || 0}</div>
          </div>
        </div>

        ${Object.keys(shiftReport.rejections_by_defect || {}).length > 0 ? `
          <div class="section no-break">
            <div class="section-title">❌ Rejeições por Tipo de Defeito</div>
            <table>
              <thead>
                <tr><th>Tipo de Defeito</th><th>Quantidade</th><th>%</th></tr>
              </thead>
              <tbody>
                ${Object.entries(shiftReport.rejections_by_defect)
                  .map(([defect, qty]) => {
                    const pct = shiftReport.summary.rejected_quantity > 0 
                      ? ((qty / shiftReport.summary.rejected_quantity) * 100).toFixed(1) 
                      : 0;
                    return `<tr><td>${defect}</td><td>${qty}</td><td>${pct}%</td></tr>`;
                  })
                  .join("")}
              </tbody>
            </table>
          </div>
        ` : ""}

        ${Object.keys(shiftReport.stops_by_reason || {}).length > 0 ? `
          <div class="section no-break">
            <div class="section-title">⏸ Paragens por Motivo</div>
            <table>
              <thead>
                <tr><th>Motivo</th><th>Ocorrências</th><th>Tempo Total</th></tr>
              </thead>
              <tbody>
                ${Object.entries(shiftReport.stops_by_reason)
                  .map(([reason, data]) => `<tr><td>${reason}</td><td>${data.count}</td><td>${formatTime(data.total_minutes)}</td></tr>`)
                  .join("")}
              </tbody>
            </table>
          </div>
        ` : ""}

        ${Object.keys(shiftReport.by_machine || {}).length > 0 ? `
          <div class="section no-break">
            <div class="section-title">🏭 Produção por Máquina</div>
            <table>
              <thead>
                <tr><th>Máquina</th><th>Ordens</th><th>Planeado</th><th>Bom</th><th>Rejeitado</th><th>Eficiência</th></tr>
              </thead>
              <tbody>
                ${Object.entries(shiftReport.by_machine)
                  .map(([machineId, data]) => {
                    const eff = data.planned > 0 ? ((data.good / data.planned) * 100).toFixed(1) : 0;
                    return `<tr>
                      <td>${machineId.substring(0, 8)}...</td>
                      <td>${data.orders?.join(", ") || "-"}</td>
                      <td>${data.planned}</td>
                      <td style="color: #16a34a; font-weight: 600;">${data.good}</td>
                      <td style="color: #dc2626;">${data.rejected}</td>
                      <td>${eff}%</td>
                    </tr>`;
                  })
                  .join("")}
              </tbody>
            </table>
          </div>
        ` : ""}

        <div class="section no-break">
          <div class="section-title">✅ Controlo de Qualidade</div>
          <div class="summary-grid" style="grid-template-columns: repeat(3, 1fr);">
            <div class="stat-card">
              <div class="stat-label">Controlos Realizados</div>
              <div class="stat-value blue">${shiftReport.quality_controls?.total || 0}</div>
            </div>
            <div class="stat-card highlight">
              <div class="stat-label">Aprovados</div>
              <div class="stat-value green">${shiftReport.quality_controls?.passed || 0}</div>
            </div>
            <div class="stat-card danger">
              <div class="stat-label">Reprovados</div>
              <div class="stat-value red">${shiftReport.quality_controls?.failed || 0}</div>
            </div>
          </div>
        </div>

        <div class="footer">
          <span>EP3D ENGINEERING PROJECT - Sistema de Gestão de Produção</span>
          <span>Relatório gerado por: ${user?.name} (${user?.user_code})</span>
          <span>Página 1 de 1</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Relatórios
          </h1>
          <p className="text-muted-foreground mt-1">
            Relatórios de produção e histórico
          </p>
        </div>
      </div>

      {/* Active Shift Info */}
      {activeShift?.active && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-4">
              <User className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Turno Ativo</p>
                <p className="font-semibold text-foreground">
                  {user?.name} - Tempo no turno: <span className="text-primary">{formatTime(activeShift.elapsed_minutes)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shift Report */}
      <Card className="border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
              <FileBarChart className="h-5 w-5" />
              Relatório de Fim de Turno
            </CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="w-40"
                  data-testid="report-date"
                />
              </div>
              <select
                value={selectedShift}
                onChange={(e) => setSelectedShift(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="report-shift"
              >
                <option value="">Todos os turnos</option>
                <option value="morning">Manhã</option>
                <option value="afternoon">Tarde</option>
                <option value="night">Noite</option>
              </select>
              <Button onClick={fetchShiftReport} data-testid="generate-report-btn">
                Gerar
              </Button>
              <Button
                variant="outline"
                onClick={generatePDF}
                disabled={!shiftReport}
                data-testid="export-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : shiftReport ? (
            <div className="space-y-6">
              {/* OEE Section */}
              <div className="p-6 bg-primary rounded-lg text-white">
                <h3 className="text-sm font-medium mb-4 opacity-90">Indicadores OEE</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="text-xs opacity-80">Disponibilidade</p>
                    <p className="text-3xl font-bold">{shiftReport.summary.availability}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs opacity-80">Performance</p>
                    <p className="text-3xl font-bold">{shiftReport.summary.performance}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs opacity-80">Qualidade</p>
                    <p className="text-3xl font-bold">{shiftReport.summary.quality}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs opacity-80">OEE Total</p>
                    <p className="text-4xl font-bold">{shiftReport.summary.oee}%</p>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Planeado</span>
                  </div>
                  <p className="font-mono text-2xl font-bold text-foreground">
                    {shiftReport.summary.planned_quantity.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-xs text-green-700">Produção Boa</span>
                  </div>
                  <p className="font-mono text-2xl font-bold text-green-600">
                    {shiftReport.summary.good_quantity.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-xs text-red-700">Rejeitado</span>
                  </div>
                  <p className="font-mono text-2xl font-bold text-red-600">
                    {shiftReport.summary.rejected_quantity.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-secondary rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Paragens</span>
                  </div>
                  <p className="font-mono text-2xl font-bold text-foreground">
                    {formatTime(shiftReport.summary.stop_time_minutes)}
                  </p>
                </div>
              </div>

              {/* Raw Materials */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">MP Teórica</p>
                  <p className="font-mono text-xl font-bold text-foreground">
                    {shiftReport.raw_materials?.theoretical_kg || 0} kg
                  </p>
                </div>
                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">MP Consumida</p>
                  <p className="font-mono text-xl font-bold text-foreground">
                    {shiftReport.raw_materials?.consumed_kg || 0} kg
                  </p>
                </div>
                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Variação</p>
                  <p className={`font-mono text-xl font-bold ${
                    (shiftReport.raw_materials?.variance_percent || 0) > 0 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {shiftReport.raw_materials?.variance_percent || 0}%
                  </p>
                </div>
              </div>

              {/* Orders Count */}
              <div className="text-sm text-muted-foreground">
                Total de ordens processadas: <span className="font-semibold text-foreground">{shiftReport.orders_count}</span>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <FileBarChart className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p>Selecione uma data e clique em "Gerar" para ver o relatório</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Production History */}
      <Card className="border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
              <Clock className="h-5 w-5" />
              Histórico de Produção
            </CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">De:</Label>
                <Input
                  type="date"
                  value={historyFilters.start_date}
                  onChange={(e) =>
                    setHistoryFilters({ ...historyFilters, start_date: e.target.value })
                  }
                  className="w-36"
                  data-testid="history-start-date"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Até:</Label>
                <Input
                  type="date"
                  value={historyFilters.end_date}
                  onChange={(e) =>
                    setHistoryFilters({ ...historyFilters, end_date: e.target.value })
                  }
                  className="w-36"
                  data-testid="history-end-date"
                />
              </div>
              <Button onClick={fetchHistory} data-testid="search-history-btn">
                Pesquisar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historyData ? (
            historyData.history.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Peça</TableHead>
                      <TableHead>Molde</TableHead>
                      <TableHead>Máquina</TableHead>
                      <TableHead>Objetivo</TableHead>
                      <TableHead>Produzido</TableHead>
                      <TableHead>Rejeitado</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyData.history.map((item) => (
                      <TableRow key={item.order.id}>
                        <TableCell className="font-mono font-bold text-foreground">
                          {item.order.code}
                        </TableCell>
                        <TableCell className="text-foreground">{item.part?.code || "N/A"}</TableCell>
                        <TableCell className="text-foreground">{item.mold?.code || "N/A"}</TableCell>
                        <TableCell className="text-foreground">{item.machine?.code || "N/A"}</TableCell>
                        <TableCell className="font-mono text-foreground">
                          {item.order.good_quantity_target.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-green-600">
                          {item.order.current_good_quantity.toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-red-600">
                          {item.order.current_rejected_quantity.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded ${
                              item.order.status === "completed"
                                ? "bg-blue-100 text-blue-700"
                                : item.order.status === "running"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {item.order.status === "completed" && "Concluída"}
                            {item.order.status === "running" && "Em Execução"}
                            {item.order.status === "pending" && "Pendente"}
                            {item.order.status === "paused" && "Pausada"}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(item.order.created_at).toLocaleDateString("pt-PT")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                Nenhum registo encontrado para o período selecionado
              </div>
            )
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p>Selecione o período e clique em "Pesquisar"</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
